import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './TelegramSettings.css';

function TelegramSettings() {
  const [linkStatus, setLinkStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [linkCode, setLinkCode] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    checkLinkStatus();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setLinkCode(null);
      setCountdown(null);
    }
  }, [countdown]);

  const checkLinkStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/telegram/link-status');
      setLinkStatus(response.data);
    } catch (error) {
      console.error('Error checking link status:', error);
      setError('Failed to check Telegram link status');
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);
      
      const response = await api.post('/telegram/generate-link-code');
      setLinkCode(response.data.code);
      setCountdown(response.data.expires_in || 600);
      
      setSuccess('Code generated! Send it to the bot in Telegram.');
    } catch (error) {
      console.error('Error generating code:', error);
      setError(error.response?.data?.detail || 'Failed to generate link code');
    } finally {
      setGenerating(false);
    }
  };

  const unlinkAccount = async () => {
    if (!window.confirm('Are you sure you want to unlink your Telegram account?')) {
      return;
    }

    try {
      setUnlinking(true);
      setError(null);
      
      await api.delete('/telegram/unlink');
      setLinkStatus({ linked: false });
      setLinkCode(null);
      setCountdown(null);
      setSuccess('Telegram account unlinked successfully');
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error unlinking:', error);
      setError(error.response?.data?.detail || 'Failed to unlink Telegram');
    } finally {
      setUnlinking(false);
    }
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="telegram-settings">
        <h2>📱 Telegram Bot</h2>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="telegram-settings">
      <h2>📱 Telegram Bot</h2>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="success-banner">
          ✓ {success}
        </div>
      )}

      <div className="settings-card">
        <div className="telegram-info">
          <p className="settings-description">
            Connect your Telegram account to quickly log expenses via the DomusAI bot (@Domusai_finance_bot).
          </p>
          
          <div className="bot-features">
            <h4>✨ Features:</h4>
            <ul>
              <li>💸 Quick expense logging: "Cena 25€"</li>
              <li>💰 Track income: "Ingreso 1500 nómina"</li>
              <li>📊 Check balance: /balance</li>
              <li>📅 View today's expenses: /today</li>
              <li>↩️ Undo last transaction: /undo</li>
            </ul>
          </div>
        </div>

        {linkStatus?.linked ? (
          // Already Linked
          <div className="telegram-linked">
            <div className="status-badge linked">
              ✓ Connected
            </div>
            
            <div className="linked-info">
              <p><strong>Telegram Username:</strong> @{linkStatus.telegram_username || 'unknown'}</p>
              <p><strong>Linked Since:</strong> {new Date(linkStatus.linked_at).toLocaleDateString()}</p>
            </div>

            <div className="telegram-actions">
              <a 
                href="https://t.me/Domusai_finance_bot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary"
              >
                💬 Open Bot
              </a>

              <button
                className="btn-danger"
                onClick={unlinkAccount}
                disabled={unlinking}
              >
                {unlinking ? 'Unlinking...' : '🔓 Unlink Account'}
              </button>
            </div>
          </div>
        ) : (
          // Not Linked
          <div className="telegram-not-linked">
            <div className="status-badge not-linked">
              ⚠️ Not Connected
            </div>

            {linkCode ? (
              // Code Generated
              <div className="link-code-container">
                <h3>🔑 Your Link Code</h3>
                
                <div className="link-code-display">
                  <code className="link-code">{linkCode}</code>
                  <button 
                    className="copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(linkCode);
                      setSuccess('Code copied to clipboard!');
                      setTimeout(() => setSuccess(null), 2000);
                    }}
                  >
                    📋 Copy
                  </button>
                </div>

                <div className="countdown-timer">
                  ⏱️ Expires in: <strong>{formatCountdown(countdown)}</strong>
                </div>

                <div className="link-instructions">
                  <h4>📝 How to link:</h4>
                  <ol>
                    <li>Open Telegram and search for <strong>@Domusai_finance_bot</strong></li>
                    <li>Start a conversation: <code>/start</code></li>
                    <li>Send: <code>/link {linkCode}</code></li>
                    <li>Done! You can now log expenses from Telegram</li>
                  </ol>
                </div>

                <a 
                  href={`https://t.me/Domusai_finance_bot?start=link_${linkCode}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary btn-large"
                >
                  🚀 Open Bot & Link Now
                </a>
              </div>
            ) : (
              // No Code Yet
              <div className="generate-code-section">
                <p className="instruction-text">
                  Click below to generate a one-time link code. You'll have 10 minutes to use it.
                </p>

                <button
                  className="btn-primary btn-large"
                  onClick={generateCode}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : '🔗 Generate Link Code'}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="telegram-help">
          <details>
            <summary>❓ Need Help?</summary>
            <div className="help-content">
              <h4>Bot Commands:</h4>
              <ul>
                <li><code>/start</code> - Start the bot</li>
                <li><code>/help</code> - Show help message</li>
                <li><code>/link CODE</code> - Link your account</li>
                <li><code>/balance</code> - View current month balance</li>
                <li><code>/today</code> - See today's transactions</li>
                <li><code>/undo</code> - Delete last transaction</li>
              </ul>

              <h4>Logging Expenses:</h4>
              <p>Just type naturally, like:</p>
              <ul>
                <li>"Cena 25€" - Food expense</li>
                <li>"Taxi 12.50" - Transport expense</li>
                <li>"Coffee 3.50" - Food expense</li>
              </ul>

              <h4>Logging Income:</h4>
              <ul>
                <li>"Ingreso 1500 nómina" - Salary income</li>
                <li>"+1500 nómina" - Also works</li>
              </ul>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

export default TelegramSettings;
